import { useEffect, useState, useMemo } from "react";
import api, { formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, Trash, ArrowClockwise, Database, Article } from "@phosphor-icons/react";

const actionColor = { create: "#10B981", update: "#2563EB", delete: "#E63946", backup: "#0A0A0A", import: "#F97316" };

export default function AuditBackup() {
  const [tab, setTab] = useState("audit");
  const [logs, setLogs] = useState([]);
  const [backups, setBackups] = useState([]);
  const [q, setQ] = useState("");
  const [entityFilter, setEntityFilter] = useState("all");
  const [running, setRunning] = useState(false);

  const loadLogs = () => {
    const params = entityFilter && entityFilter !== "all" ? `?entity=${entityFilter}` : "";
    api.get("/audit-logs" + params).then((r) => setLogs(r.data));
  };
  const loadBackups = () => api.get("/backups").then((r) => setBackups(r.data));

  useEffect(() => { loadLogs(); loadBackups(); }, [entityFilter]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return logs;
    return logs.filter((l) =>
      (l.user_email || "").toLowerCase().includes(s) ||
      (l.user_name || "").toLowerCase().includes(s) ||
      (l.action || "").toLowerCase().includes(s) ||
      (l.entity || "").toLowerCase().includes(s) ||
      JSON.stringify(l.details || {}).toLowerCase().includes(s)
    );
  }, [logs, q]);

  const runBackup = async () => {
    setRunning(true);
    try {
      const r = await api.post("/backups/run");
      toast.success(`Backup dibuat: ${r.data.filename}`);
      loadBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal backup");
    } finally { setRunning(false); }
  };

  const downloadBackup = async (fn) => {
    try {
      const r = await api.get(`/backups/${fn}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = fn;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal download"); }
  };

  const deleteBackup = async (fn) => {
    if (!window.confirm(`Hapus backup ${fn}?`)) return;
    await api.delete(`/backups/${fn}`);
    toast.success("Terhapus"); loadBackups();
  };

  const restoreBackup = async (fn) => {
    if (!window.confirm(`⚠️ PERINGATAN: Restore akan MENIMPA semua data saat ini dengan isi backup "${fn}".\n\nSemua transaksi/pembayaran/data yang dibuat SETELAH backup ini akan HILANG. Yakin lanjut?`)) return;
    if (!window.confirm(`Konfirmasi sekali lagi: Restore ${fn}?`)) return;
    try {
      const r = await api.post(`/backups/${fn}/restore`);
      toast.success(`Restore selesai. Koleksi: ${Object.keys(r.data.restored).length}`);
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal restore");
    }
  };

  const formatSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div>
      <PageHeader
        title="Audit & Backup"
        subtitle="Log aktivitas user dan backup data showroom"
        actions={
          tab === "backup" && (
            <Button onClick={runBackup} disabled={running} data-testid="run-backup-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
              <ArrowClockwise size={14} className="mr-2" /> {running ? "Membackup..." : "Backup Sekarang"}
            </Button>
          )
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <div className="flex gap-0 border border-gray-200 -mb-px">
          <button
            onClick={() => setTab("audit")}
            data-testid="tab-audit"
            className={`px-6 py-4 text-xs uppercase tracking-wider border-r border-gray-200 flex items-center gap-2 ${tab === "audit" ? "bg-[#0A0A0A] text-white font-semibold" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            <Article size={14} weight="bold" /> Log Aktivitas
          </button>
          <button
            onClick={() => setTab("backup")}
            data-testid="tab-backup"
            className={`px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 ${tab === "backup" ? "bg-[#0A0A0A] text-white font-semibold" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            <Database size={14} weight="bold" /> Backup Data
          </button>
        </div>

        <div className="border border-gray-200 p-6">
          {tab === "audit" ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 max-w-md">
                  <SearchBar value={q} onChange={setQ} placeholder="Cari user / aksi / detail..." testid="audit-search" count={filtered.length} />
                </div>
                <div className="min-w-[200px]">
                  <Select value={entityFilter} onValueChange={setEntityFilter}>
                    <SelectTrigger className="rounded-none" data-testid="entity-filter"><SelectValue placeholder="Semua entitas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Entitas</SelectItem>
                      <SelectItem value="sale">Penjualan</SelectItem>
                      <SelectItem value="payment">Pembayaran</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="system">Sistem</SelectItem>
                      <SelectItem value="backup">Backup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                      <th className="p-4">Waktu</th>
                      <th className="p-4">User</th>
                      <th className="p-4">Aksi</th>
                      <th className="p-4">Entitas</th>
                      <th className="p-4">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => (
                      <tr key={l.id} className="border-b border-gray-100">
                        <td className="p-4 font-mono-num text-xs text-gray-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="p-4">
                          <div className="font-semibold text-xs">{l.user_name || "-"}</div>
                          <div className="text-xs text-gray-500 font-mono-num">{l.user_email}</div>
                        </td>
                        <td className="p-4">
                          <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-wider text-white font-semibold" style={{ backgroundColor: actionColor[l.action] || "#6B7280" }}>
                            {l.action}
                          </span>
                        </td>
                        <td className="p-4 text-xs uppercase tracking-wider text-gray-700">{l.entity}</td>
                        <td className="p-4 text-xs font-mono-num text-gray-600 max-w-lg truncate">{JSON.stringify(l.details || {})}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan="5" className="p-10 text-center text-sm text-gray-500">Belum ada log aktivitas</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 border border-gray-200 p-4 bg-gray-50">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-2">
                  <Database size={12} weight="bold" /> Jadwal Otomatis
                </div>
                <div className="text-sm">
                  Backup otomatis setiap malam <span className="font-mono-num font-semibold">02:00 WIB</span> — menyimpan snapshot semua data ke file JSON.
                  Retensi <span className="font-mono-num font-semibold">30 backup</span> terakhir (yang lebih lama dihapus otomatis).
                </div>
              </div>
              <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                      <th className="p-4">Nama File</th>
                      <th className="p-4">Waktu</th>
                      <th className="p-4 text-right">Ukuran</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map((b) => (
                      <tr key={b.filename} className="border-b border-gray-100" data-testid={`backup-row-${b.filename}`}>
                        <td className="p-4 font-mono-num text-xs font-semibold">{b.filename}</td>
                        <td className="p-4 font-mono-num text-xs text-gray-600">{new Date(b.created_at).toLocaleString("id-ID")}</td>
                        <td className="p-4 text-right font-mono-num text-xs">{formatSize(b.size)}</td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <button onClick={() => downloadBackup(b.filename)} data-testid={`backup-dl-${b.filename}`} className="mr-3 text-gray-500 hover:text-black inline-flex items-center gap-1 text-xs uppercase tracking-wider">
                            <DownloadSimple size={12} /> Download
                          </button>
                          <button onClick={() => restoreBackup(b.filename)} data-testid={`backup-restore-${b.filename}`} className="mr-3 text-[#F97316] hover:text-orange-700 inline-flex items-center gap-1 text-xs uppercase tracking-wider">
                            <ArrowClockwise size={12} /> Restore
                          </button>
                          <button onClick={() => deleteBackup(b.filename)} className="text-gray-500 hover:text-[#E63946]" data-testid={`backup-del-${b.filename}`}>
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {backups.length === 0 && (
                      <tr><td colSpan="4" className="p-10 text-center text-sm text-gray-500">Belum ada backup — klik "Backup Sekarang"</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
